import { motion } from 'framer-motion';
import { Sparkles, RefreshCcw, Wrench, CreditCard } from 'lucide-react';

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Free Placement',
    desc: 'We install machines at no cost to you. No fees, no contracts with hidden clauses.',
  },
  {
    icon: RefreshCcw,
    title: 'Full Restocking',
    desc: 'We monitor inventory and restock regularly so your machine is always full.',
  },
  {
    icon: Wrench,
    title: 'Maintenance',
    desc: 'If anything breaks, we fix it. Fast response times, zero downtime for you.',
  },
  {
    icon: CreditCard,
    title: 'Cashless Payments',
    desc: 'Card, Apple Pay & Google Pay accepted on every machine we place.',
  },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export const WhatWeDo = () => {
  return (
    <section id="what-we-do" className="scroll-mt-24 bg-white py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-10 lg:gap-16">
          {/* Left Column */}
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <span className="text-xs uppercase tracking-[0.22em] text-kv-gold font-sans font-semibold mb-4 block">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-kv-black-2 leading-snug mb-6">
              We place, stock, and manage &mdash; you just enjoy the service.
            </h2>
            <p className="text-base text-gray-500 leading-relaxed max-w-lg font-sans">
              There's <strong className="text-kv-black-2">no upfront cost</strong> and{' '}
              <strong className="text-kv-black-2">no maintenance on your end</strong>. We handle
              everything from installation to restocking. Your business gets a premium vending
              experience with zero hassle.
            </p>
          </motion.div>

          {/* Right Column - 2x2 Grid */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {FEATURES.map((feature) => (
              <motion.div
                key={feature.title}
                variants={cardVariants}
                className="rounded-2xl border border-black/8 bg-white p-6 shadow-[0_1px_0_rgba(17,17,20,0.04),0_10px_30px_rgba(17,17,20,0.05)] hover:-translate-y-0.5 transition-transform"
              >
                <div className="w-10 h-10 rounded-xl bg-kv-gold/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-kv-gold" />
                </div>
                <h3 className="text-base font-semibold text-kv-black-2 mb-2 font-sans">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-sans">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
